import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Receipt } from "lucide-react";
import { formatCurrency } from "../../lib/format";

/**
 * CommissionCalculator - Calculates broker commission from premium and total collected
 * Used in PDF Import, Email Import
 * 
 * Premium = premio polizza + costo servizi aggiuntivi
 */
export function CommissionCalculator({ 
  premium, 
  totalCollected, 
  consultationFee,
  additionalServices = [],
  onTotalCollectedChange 
}) {
  const basePremium = parseFloat(premium) || 0;
  const servicesTotal = (additionalServices || []).reduce(
    (sum, svc) => sum + (parseFloat(svc.cost) || 0), 0
  );
  const premiumValue = basePremium + servicesTotal;
  const feeValue = parseFloat(consultationFee) || 0;

  // Recalculate commission when services change and totalCollected exists
  useEffect(() => {
    if (totalCollected) {
      const totalValue = parseFloat(totalCollected) || 0;
      const commission = Math.max(0, totalValue - premiumValue);
      if (commission.toFixed(2) !== (parseFloat(consultationFee) || 0).toFixed(2)) {
        onTotalCollectedChange(totalCollected, commission.toFixed(2));
      }
    }
  }, [servicesTotal]);

  const handleTotalChange = (e) => {
    const totalValue = parseFloat(e.target.value) || 0;
    const commission = Math.max(0, totalValue - premiumValue);
    onTotalCollectedChange(e.target.value, commission.toFixed(2));
  };

  return (
    <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-5 w-5 text-green-600" />
          Calcolo Commissioni
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs text-muted-foreground">Premio Totale</Label>
            <div className="text-lg font-bold text-foreground" data-testid="commission-premium">
              {formatCurrency(premiumValue)}
            </div>
            {servicesTotal > 0 && (
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="commission-breakdown">
                Polizza {formatCurrency(basePremium)} + Servizi {formatCurrency(servicesTotal)}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Totale Incassato dal Cliente</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Es: 550.00"
              value={totalCollected || ""}
              onChange={handleTotalChange}
              className="bg-white dark:bg-background"
              data-testid="commission-total-input"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Commissioni Calcolate</Label>
            <div className="text-lg font-bold text-green-600" data-testid="commission-result">
              {formatCurrency(feeValue)}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Commissioni = Totale Incassato - Premio Totale (polizza + servizi aggiuntivi)
        </p>
      </CardContent>
    </Card>
  );
}

export default CommissionCalculator;
